import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { referenceServers, thirdPartyServers, communityServers } from '../data/mcpData';
import referenceServersJson from '../data/referenceServers/readme.json';
import thirdPartyServersJson from '../data/thirdPartyServers/readme.json';
import communityServersJson from '../data/communityServers/readme.json';
import '../styles/ReadmeContent.css';

const ServerDetail = () => {
    const { serverName } = useParams();
    const navigate = useNavigate();
    const [server, setServer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [readmeContent, setReadmeContent] = useState('');
    const [readmeLoading, setReadmeLoading] = useState(true);

    useEffect(() => {
        // Find the server by name
        const decodedName = decodeURIComponent(serverName);
        const foundServer = [...referenceServers, ...thirdPartyServers, ...communityServers].find(s => s.name === decodedName);

        if (foundServer) {
            setServer(foundServer);
            setImageError(false); // Reset image error state when changing servers

            // Find matching readme file
            if (foundServer.githubUrl) {
                let fileDir = ''
                let fileData = {}

                const matchRefData = referenceServersJson.find(r => r.url === foundServer.githubUrl)
                if (matchRefData) {
                    fileDir = 'referenceServers'
                    fileData = matchRefData
                }

                const matchThiData = thirdPartyServersJson.find(r => r.url === foundServer.githubUrl)
                if (matchThiData) {
                    fileDir = 'thirdPartyServers'
                    fileData = matchThiData
                }

                const matchComData = communityServersJson.find(r => r.url === foundServer.githubUrl)
                if (matchComData) {
                    fileDir = 'communityServers'
                    fileData = matchComData
                }

                if (fileDir || Object.keys(fileData).length) {
                    // Load the readme HTML file
                    setReadmeLoading(true);
                    fetch(`/data/${fileDir}/${fileData.html_file}`)
                        .then(response => {
                            if (!response.ok) {
                                throw new Error(`Failed to load readme file: ${response.status} ${response.statusText}`);
                            }
                            return response.text();
                        })
                        .then(html => {
                            setReadmeContent(html);
                            setReadmeLoading(false);
                        })
                        .catch(error => {
                            setReadmeContent('');
                            setReadmeLoading(false);
                        });
                } else {
                    setReadmeContent('');
                    setReadmeLoading(false);
                }
            } else {
                setReadmeContent('');
                setReadmeLoading(false);
            }
        }

        setLoading(false);
    }, [serverName]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <motion.div
                    className="rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"
                    animate={{ rotate: 360 }}
                    transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                />
            </div>
        );
    }

    if (!server) {
        return (
            <motion.div
                className="flex flex-col items-center justify-center py-20"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
            >
                <h2 className="text-2xl font-bold mb-4 text-gray-800">Server Not Found</h2>
                <p className="text-gray-600 mb-8">Cannot find a server named "{decodeURIComponent(serverName)}"</p>
                <motion.button
                    onClick={() => navigate('/servers')}
                    className="bg-primary hover:bg-blue-600 transition-colors text-white font-medium py-2 px-6 rounded-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Back to Server List
                </motion.button>
            </motion.div>
        );
    }

    // Generate a placeholder image based on server name
    const generatePlaceholderImage = (name) => {
        // Create a hash-like string from the name to use for colors
        const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
        const hue = hash % 360;

        return `https://via.placeholder.com/400x200/${hash.toString(16).slice(0, 6)}/FFFFFF?text=${encodeURIComponent(name)}`;
    };

    // Generate inclusion date (for demo purposes)
    const generateInclusionDate = (name) => {
        // Use server name to generate a consistent but random-looking date
        const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
        const day = 1 + (hash % 28);
        const month = 1 + (hash % 12);
        const year = 2022 + (hash % 3);

        return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    };

    // Check if server has features
    const hasFeatures = server.features && server.features.length > 0;

    // Check if server has usage information
    const hasUsage = server.usage && server.usage.length > 0;

    // Check if server has a valid image
    const hasImage = server.image && typeof server.image === 'string' && server.image.trim() !== '';

    // Get image source - use server.image if it exists and hasn't errored, otherwise use placeholder
    const getImageSource = () => {
        if (hasImage && !imageError) {
            return server.image;
        }
        return generatePlaceholderImage(server.name);
    };

    // Extract domain from URL
    const getDomain = (url) => {
        try {
            if (!url) return '';
            const domain = new URL(url.replace(/\/blob\/.*$/, '')).hostname.replace('www.', '');
            return domain;
        } catch (e) {
            return url;
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-6xl mx-auto"
        >
            <div className="mb-6">
                <motion.button
                    className="text-gray-600 hover:text-primary flex items-center transition-colors"
                    onClick={() => navigate('/servers')}
                    whileHover={{ x: -5 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Server List
                </motion.button>
            </div>

            {/* Server image - full width */}
            <motion.div
                className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden mb-6"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
                {hasImage && (
                    <div className="w-full h-64 bg-gray-100 overflow-hidden">
                        <motion.img
                            src={getImageSource()}
                            alt={server.name}
                            className="w-full h-full object-contain"
                            initial={{ scale: 1.1 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.5 }}
                            onError={() => setImageError(true)}
                        />
                    </div>
                )}

                {/* Title and inclusion date */}
                <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                        <motion.h1
                            className="text-2xl md:text-3xl font-bold text-gray-800"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            {server.detailTitle || server.name}
                        </motion.h1>
                    </div>
                    <motion.p
                        className="text-gray-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        {server.description}
                    </motion.p>
                </div>
            </motion.div>

            {/* Two-column layout */}
            <div className="flex flex-col lg:flex-row gap-6">
                {/* Left column - Main content */}
                <motion.div
                    className="lg:w-2/3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
                        {/* README content section */}
                        <motion.div
                            className="p-6"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">Documentation</h2>
                            {readmeLoading ? (
                                <div className="flex justify-center items-center h-64">
                                    <motion.div
                                        className="rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"
                                        animate={{ rotate: 360 }}
                                        transition={{
                                            duration: 1.5,
                                            repeat: Infinity,
                                            ease: "linear"
                                        }}
                                    />
                                </div>
                            ) : readmeContent ? (
                                <div
                                    className="readme-content"
                                    dangerouslySetInnerHTML={{ __html: readmeContent }}
                                />
                            ) : (
                                <div className="text-gray-500">
                                    <p className="mb-4">
                                        No documentation available. You can check the basic information below:
                                    </p>
                                    <div className="space-y-4">
                                        <p><strong>Description:</strong> {server.description}</p>
                                        {server.intro && <p><strong>Introduction:</strong> {server.intro}</p>}
                                        {hasFeatures && (
                                            <div>
                                                <strong>Features:</strong>
                                                <ul className="list-disc ml-5 mt-2">
                                                    {server.features.map((feature, index) => (
                                                        <li key={index}>{feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {server.githubUrl && (
                                            <p>
                                                <strong>For more information:</strong>{' '}
                                                <a
                                                    href={server.githubUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline"
                                                >
                                                    View GitHub Repository
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </motion.div>

                        {/* Usage section - conditionally rendered */}
                        {hasUsage && (
                            <motion.div
                                className="p-6 border-t border-gray-100"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                            >
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">How to Use</h2>
                                <div className="space-y-4 text-gray-700">
                                    <ul className="space-y-3">
                                        {server.usage.map((usage, index) => (
                                            <li key={index} className="flex items-start">
                                                <svg className="w-5 h-5 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                                </svg>
                                                <span className="text-gray-700">{usage}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                {/* Right column - Sidebar */}
                <motion.div
                    className="lg:w-1/3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    {/* Homepage section */}
                    {server.githubUrl && (
                        <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden mb-6">
                            <div className="p-6 border-b border-gray-100">
                                <h2 className="text-xl font-semibold text-gray-800">Homepage</h2>
                            </div>
                            <div className="p-6">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mr-3">
                                        <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Hosted on</span>
                                        <div className="font-medium">{getDomain(server.githubUrl)}</div>
                                    </div>
                                </div>
                                <a
                                    href={server.githubUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full bg-primary hover:bg-blue-600 text-white font-medium py-2 px-4 rounded text-center transition-colors"
                                >
                                    View Source Code
                                </a>
                                <p className="text-sm text-gray-500 mt-3">
                                    View the complete implementation of this MCP server on GitHub, including source code and detailed documentation.
                                </p>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </motion.div>
    );
};

export default ServerDetail;